import { MediaPlayer, MediaProvider, type MediaTimeUpdateEventDetail, Poster, SeekButton, Tooltip } from "@vidstack/react";
import { DefaultAudioLayout, defaultLayoutIcons, DefaultVideoLayout } from "@vidstack/react/player/layouts/default";
import type {
	CoachReviewPromises,
	GameClipIntervalRequest,
	GameEventCommentProps,
	GameEventDataExportRequest,
	GamePageLazyResponse,
	GPGamePlayerResponse,
	GamePageResponse,
	GPGameTeamResponse,
	HighlightHandlers,
	PlayerGradingSubmitPayload,
	RatingPutRequest,
} from "../../types";
import type { ReportEventErrorHandler } from "../../report-error";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useVideoPlayer } from "../../Contexts/contexts";
import { Subject, throttleTime } from "rxjs";
import { VideoPlayerSidebar } from "./VideoPlayer/VideoPlayerSidebar";
import { VideoPlayerControls } from "./VideoPlayer/VideoPlayerControls";
import { useTimeStore } from "../../time-store";
import { useGameStore } from "../../store";
import { VideoSidebar, NavOptions } from "../../types";
import { useRestoreScrollOnFullscreenExit } from "../../useRestoreScrollOnFullscreenExit";
import { GAME_FILTERED_STATE_KEY } from "../DataInsights/constants";
import { handlePlayEvent } from "../../functions";
import { Button } from "../../../../../ui-kit-v2/src/button/button";
import { ArrowLeft } from "lucide-react";
import { _ } from "i18n";
import { UserDownloadActivityTags } from "../../../../../../assets/js/types";
import { useVideoPlayerVolumeStore } from "../../../video-player/video-player-volume-store";

interface VideoPlayerProps {
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	showCoachingTools: boolean;
	highlightsHandlers: HighlightHandlers;
	handleCreateClip: (intervals: GameClipIntervalRequest[], name: string, description?: string) => void;
	handleOpenEditTeamSheetModal: (team: GPGameTeamResponse) => void;
	handleGameEventDataExport: (gameEventDataExportRequest: GameEventDataExportRequest) => void;
	handleTeamSheetDataExport: (type: string) => void;
	handleChangeNavOptions: (tab: NavOptions) => void;
	storeDownloadVideoActivity: (tag: UserDownloadActivityTags, gameId: number) => void;
	handleSetPlayerRating?: (id: number, rating: RatingPutRequest) => void;
	handleSubmitPlayerGrading?: (payload: PlayerGradingSubmitPayload) => void | Promise<void>;
	toastMessage: (type: string, message: string) => void;
	gameEventComment?: GameEventCommentProps;
	players?: GPGamePlayerResponse[];
	onReportError?: ReportEventErrorHandler;
	coachReviewPromises?: CoachReviewPromises;
	userId?: number;
	activeContextTeamId?: number;
}

export function VideoPlayer({
	gameData,
	gameDataLazy,
	showCoachingTools,
	handleOpenEditTeamSheetModal,
	highlightsHandlers,
	handleCreateClip,
	handleGameEventDataExport,
	handleTeamSheetDataExport,
	handleChangeNavOptions,
	storeDownloadVideoActivity,
	handleSetPlayerRating,
	handleSubmitPlayerGrading,
	toastMessage,
	gameEventComment,
	players,
	onReportError,
	coachReviewPromises,
	userId,
	activeContextTeamId,
}: VideoPlayerProps) {
	const [fullscreen, setFullscreen] = useState<boolean>();
	const { setSidebar, setFilteredFilterValues, setEvents, eventFilter, setFullscreen: setStoreFullscreen } = useGameStore();
	const [show, setShow] = useState(false);
	const { playerRef, canPlay, setIsPlaying, isPlaying } = useVideoPlayer();
	const [backToInsightButton, setBackToInsightButton] = useState(false);

	const { setCurrentTime } = useTimeStore();

	const volume = useVideoPlayerVolumeStore((state) => state.volume);
	const muted = useVideoPlayerVolumeStore((state) => state.muted);
	const setVolume = useVideoPlayerVolumeStore((state) => state.setVolume);

	const timeSubject = useMemo(() => new Subject<number>(), []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const subscription = timeSubject.pipe(throttleTime(250)).subscribe((time) => {
			setCurrentTime(time);
		});
		return () => subscription.unsubscribe();
	}, [timeSubject]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!gameDataLazy) return;
		const matchStartEvent = gameDataLazy.events.find((e) => e.half === 1 && e.type === "start_of_half");
		if (canPlay && matchStartEvent !== undefined && playerRef?.current) {
			playerRef.current.currentTime = matchStartEvent.videoTime / 1000;
		}
	}, [canPlay, gameDataLazy]);

	function onTimeUpdate(nativeEventDetail: MediaTimeUpdateEventDetail) {
		if (nativeEventDetail.currentTime && playerRef?.current && !playerRef.current.paused) {
			timeSubject.next(nativeEventDetail.currentTime * 1000);
		}
	}

	useEffect(() => {
		if (fullscreen !== undefined) {
			const player = document.getElementById("player");
			player?.focus();
		}
	}, [fullscreen]);

	useRestoreScrollOnFullscreenExit(fullscreen);

	const pendingSeekTimeRef = useRef<number | null>(null);

	// Apply saved state from Data Insights as soon as we have gameDataLazy so the list shows
	// the correct event on first paint (don't wait for canPlay, which causes "one behind").
	// eslint-disable-next-line react-hooks/exhaustive-deps -- only run when gameDataLazy is set; eventFilter/setters are stable
	useEffect(() => {
		if (!gameDataLazy) return;
		const savedState = localStorage.getItem(GAME_FILTERED_STATE_KEY) || null;
		if (!savedState) return;
		const { filterValuesData, eventTime } = JSON.parse(savedState);
		setEvents(eventFilter.filter(filterValuesData), filterValuesData);
		setFilteredFilterValues(filterValuesData);
		setSidebar(VideoSidebar.EventList);
		pendingSeekTimeRef.current = typeof eventTime === "number" ? eventTime : null;
		localStorage.removeItem(GAME_FILTERED_STATE_KEY);
		setBackToInsightButton(true);
	}, [gameDataLazy]);

	// Seek and play once the player is ready (canPlay). Running this before the ref is attached
	// would no-op and the video would start from the beginning.
	useEffect(() => {
		if (!canPlay || pendingSeekTimeRef.current === null) return;
		const timeMs = pendingSeekTimeRef.current;
		pendingSeekTimeRef.current = null;
		requestAnimationFrame(() => {
			handlePlayEvent(playerRef, timeMs);
		});
	}, [canPlay, playerRef]);

	const downloadVideo = (videoUrl: string) => {
		window.location.replace(videoUrl);
		storeDownloadVideoActivity(UserDownloadActivityTags.VIDEO, gameData?.game?.id);
	};

	return (
		<div className="videoPlayer-container">
			<MediaPlayer
				id="player"
				ref={playerRef}
				src={gameData.video?.url ?? ""}
				onFullscreenChange={(v) => {
					setFullscreen(v);
					setStoreFullscreen(v ?? false);
				}}
				fullscreenOrientation="none"
				onTimeUpdate={onTimeUpdate}
				playsInline
				volume={volume}
				muted={muted}
				onVolumeChange={setVolume}
				onPlay={setIsPlaying.bind(null, true)}
				onPause={setIsPlaying.bind(null, false)}
				autoPlay={isPlaying}
			>
				<MediaProvider>
					<Poster className="vds-poster" src={gameData.video?.poster} />
				</MediaProvider>

				<VideoPlayerSidebar
					gameDataLazy={gameDataLazy}
					showCoachingTools={showCoachingTools}
					handleCreateClip={handleCreateClip}
					highlightsHandlers={highlightsHandlers}
					handleOpenEditTeamSheetModal={handleOpenEditTeamSheetModal}
					handleGameEventDataExport={handleGameEventDataExport}
					handleTeamSheetDataExport={handleTeamSheetDataExport}
					fullscreen={fullscreen}
					show={show}
					playerRef={playerRef}
					handleSetPlayerRating={handleSetPlayerRating}
					handleSubmitPlayerGrading={handleSubmitPlayerGrading}
					toastMessage={toastMessage}
					gameEventComment={gameEventComment}
					players={players}
					onReportError={onReportError}
					coachReviewPromises={coachReviewPromises}
					userId={userId}
					activeContextTeamId={activeContextTeamId}
				/>

				<VideoPlayerControls fullscreen={fullscreen} show={show} onShowChange={setShow} />
				<div
					id="fullscreen-modal-portal"
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 100000,
						pointerEvents: "none",
					}}
					className="fullscreen-modal-portal"
				/>
				<DefaultAudioLayout icons={defaultLayoutIcons} />
				<DefaultVideoLayout
					icons={defaultLayoutIcons}
					noAudioGain
					playbackRates={{ min: 0, max: 4, step: 0.25 }}
					slots={{
						largeLayout: {
							beforePlayButton: (
								<Tooltip.Root>
									<Tooltip.Trigger asChild>
										<SeekButton className="vds-button" seconds={-5}>
											<span className="icon-new icon-seek-backward icon-32 vds-icon" />
										</SeekButton>
									</Tooltip.Trigger>
									<Tooltip.Content className="vds-tooltip-content" placement="top">
										Seek -5
									</Tooltip.Content>
								</Tooltip.Root>
							),
							afterPlayButton: (
								<Tooltip.Root>
									<Tooltip.Trigger asChild>
										<SeekButton className="vds-button" seconds={5}>
											<span className="icon-new icon-seek-forward icon-32 vds-icon" />
										</SeekButton>
									</Tooltip.Trigger>
									<Tooltip.Content className="vds-tooltip-content" placement="top">
										Seek +5
									</Tooltip.Content>
								</Tooltip.Root>
							),
							...(gameDataLazy?.videoDownloadUrl && {
								downloadButton: (
									<Tooltip.Root>
										<Tooltip.Trigger asChild>
											<a
												className="vds-button"
												href={gameDataLazy.videoDownloadUrl}
												onTouchEnd={() => window.location.replace(String(gameDataLazy?.videoDownloadUrl))}
												onClick={() => downloadVideo(String(gameDataLazy?.videoDownloadUrl))}
											>
												{defaultLayoutIcons.DownloadButton?.Default({})}
											</a>
										</Tooltip.Trigger>
										<Tooltip.Content className="vds-tooltip-content" placement="top">
											Download
										</Tooltip.Content>
									</Tooltip.Root>
								),
							}),
							// timeSlider:
							//     <TimeSlider.Root className="vds-slider">
							//         <TimeSlider.Preview className="vds-slider-preview">
							//             <TimeSlider.Thumbnail.Root
							//                 src={gameData.video?.thumbs}
							//                 className="vds-thumbnail"
							//             >
							//                 <Thumbnail.Img />
							//             </TimeSlider.Thumbnail.Root>
							//             <TimeSlider.Value />
							//         </TimeSlider.Preview>
							//         <TimeSlider.Track className="vds-slider-track" >
							//         </TimeSlider.Track >
							//         <TimeSlider.TrackFill className="vds-slider-track-fill vds-slider-track" />
							//         <TimeSlider.Thumb className="vds-slider-thumb" />
							//     </TimeSlider.Root>,
						},
					}}
				/>
			</MediaPlayer>
			{backToInsightButton && (
				<div className="back-to-insight-button-container">
					<Button
						className="btn btn--grey back-to-insight-button"
						iconLeft={<ArrowLeft size={16} />}
						text={_("gamePage.label.dataInsights")}
						onClick={() => {
							handleChangeNavOptions(NavOptions.DataInsights);
						}}
					/>
				</div>
			)}
		</div>
	);
}
